/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable array-callback-return */
import NutrientViewer, {
  AnnotationsUnion,
  FormField,
  Instance,
  List,
  ToolbarItem,
} from '@nutrient-sdk/viewer';
import { cloneDeep, isNull, isUndefined } from 'lodash';
import getAnnotationRenderers from './customAnnotationRenderer';
import { duplicateAnnotationCallback } from './helpers';
import { TAssignee } from './types';

export type CustomInstantJSON = {
  format: 'https://pspdfkit.com/instant-json/v1';
  customFromAPI: boolean;
};

export const TOOLBAR_ITEMS: ToolbarItem[] = [
  { type: 'sidebar-thumbnails', mediaQueries: ['(min-width: 577px)'] },
  { type: 'pager' },
  { type: 'zoom-out' },
  { type: 'zoom-in' },
  { type: 'search' },
  { type: 'spacer' },
  { type: 'print', mediaQueries: ['(min-width: 577px)'] },
];

export const getPreviewToolbarItems = (
  instance: Instance,
  fileUrl?: string,
): ToolbarItem[] => {
  return [
    ...TOOLBAR_ITEMS,
    {
      type: 'custom',
      id: 'preview-download',
      icon: `${window.location.protocol}//${window.location.host}/assets/download.svg`,
      title: 'Download',
      onPress: () => {
        if (!fileUrl) {
          return;
        }

        const fileName = getFilenameFromUrl(fileUrl);

        instance.exportPDF().then((buffer: ArrayBuffer) => {
          const blob = new Blob([buffer], { type: 'application/pdf' });
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');

          a.href = objectUrl;
          a.style.display = 'none';
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          URL.revokeObjectURL(objectUrl);
          document.body.removeChild(a);
        });
      },
    } as ToolbarItem,
    {
      type: 'custom',
      id: 'rotate-left',
      title: 'Rotate Left',
      icon: `${window.location.protocol}//${window.location.host}/assets/rotate-left.svg`,
      onPress: (event) => {
        if (instance) {
          instance.applyOperations([
            {
              type: 'rotatePages',
              pageIndexes: [instance.viewState.currentPageIndex],
              rotateBy: 270, // Rotate counter-clockwise
            },
          ]);
        }
      },
    },
    {
      type: 'custom',
      id: 'rotate-right',
      title: 'Rotate Right',
      icon: `${window.location.protocol}//${window.location.host}/assets/rotate-right.svg`,
      onPress: (event) => {
        if (instance) {
          instance.applyOperations([
            {
              type: 'rotatePages',
              pageIndexes: [instance.viewState.currentPageIndex],
              rotateBy: 90, // Rotate clockwise
            },
          ]);
        }
      },
    },
  ];
};

export const getFilenameFromUrl = (url: string): string => {
  const queryString = url.includes('?') ? url.split('?')[1] : null;

  if (!queryString) {
    return 'document.pdf';
  }

  const urlParams = new URLSearchParams(queryString);
  const contentDisposition = urlParams.get('response-content-disposition');

  if (contentDisposition) {
    // Match filename with proper quote handling - capture everything between filename= and the next semicolon
    const filenameMatch = contentDisposition.match(/filename=([^;]+)/);

    if (filenameMatch && filenameMatch[1]) {
      // Remove surrounding quotes if they exist
      const filename = filenameMatch[1].replace(/^["']|["']$/g, '');
      return decodeURIComponent(filename);
    }
  }
  return 'document.pdf';
};

/**
 * Interface for form field options
 */
interface FormOption {
  label: string | null;
  value: string | null;
}

/**
 * Type guard to check if an object is a valid FormOption
 */
const isFormOption = (opt: unknown): opt is FormOption => {
  return (
    typeof opt === 'object' && opt !== null && 'label' in opt && 'value' in opt
  );
};

/**
 * Validates if a form option (radio button or checkbox) has valid label and value
 */
const isValidFormOption = (opt: unknown): boolean => {
  if (!isFormOption(opt)) {
    return false;
  }

  return (
    opt.label !== null &&
    opt.value !== null &&
    opt.label !== undefined &&
    opt.value !== undefined &&
    opt.label !== '' &&
    opt.value !== '' &&
    Boolean(opt.label) &&
    Boolean(opt.value)
  );
};

const getDefaultFormOption = (optionNumber?: number, valueNumber?: number) => ({
  label: `Option ${optionNumber ?? 1}`,
  value: `Value ${valueNumber ?? 1}`,
});

/**
 * Normalizes a value to an array, handling arrays, Immutable Lists, and single values
 */
const normalizeToArray = (value: any): any[] => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (value.toArray && typeof value.toArray === 'function') {
    return value.toArray();
  }
  return [value];
};

/**
 * Validates and cleans up form field options (radio button or checkbox)
 */
const validateAndCleanupOptions = (options: any): any[] => {
  const optionsArray = normalizeToArray(options);
  const validOptions = optionsArray.filter((opt: any) =>
    isValidFormOption(opt),
  );

  return validOptions.length > 0 ? validOptions : [getDefaultFormOption()];
};

/**
 * Validates annotationIds against a set of valid annotation IDs
 */
const validateAnnotationIds = (
  annotationIds: any,
  validAnnotationIds: Set<string>,
  fieldName: string,
  fieldType: string,
): any[] => {
  const annotationIdsArray = normalizeToArray(annotationIds);
  const validIds: any[] = [];

  for (const id of annotationIdsArray) {
    const idString = String(id);
    if (validAnnotationIds.has(idString)) {
      // Preserve original ID type (could be number, string, etc.) like original filter() did
      validIds.push(id);
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        `[cleanupFormFields] Removing invalid annotationId "${idString}" from ${fieldType} field "${fieldName}"`,
      );
    }
  }

  if (validIds.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[cleanupFormFields] ${fieldType} field "${fieldName}" has no valid annotationIds`,
    );
  }

  return validIds;
};

/**
 * Aligns options count with annotationIds count (1:1 relationship)
 * Note: This is primarily for radio buttons. Checkboxes may have different relationships.
 */
const alignOptionsWithAnnotationIds = (
  options: any[],
  annotationIdsCount: number,
  fieldName: string,
  fieldType: string,
): any[] => {
  const targetOptionsCount = annotationIdsCount > 0 ? annotationIdsCount : 1;
  const currentOptionsCount = options.length;

  if (currentOptionsCount === targetOptionsCount) {
    return options;
  }

  if (currentOptionsCount > targetOptionsCount) {
    // eslint-disable-next-line no-console
    console.warn(
      `[cleanupFormFields] Trimming options from ${currentOptionsCount} to ${targetOptionsCount} to match annotationIds count for ${fieldType} field "${fieldName}"`,
    );
    return options.slice(0, targetOptionsCount);
  }

  // Add default options to match annotationIds count
  const optionsToAdd = targetOptionsCount - currentOptionsCount;
  const additionalOptions = Array.from({ length: optionsToAdd }, (_, index) =>
    getDefaultFormOption(
      currentOptionsCount + index + 1,
      currentOptionsCount + index + 1,
    ),
  );

  // eslint-disable-next-line no-console
  console.warn(
    `[cleanupFormFields] Adding ${optionsToAdd} default option(s) to match ${targetOptionsCount} ${annotationIdsCount > 0 ? 'annotationIds' : 'minimum required'} for ${fieldType} field "${fieldName}"`,
  );

  return [...options, ...additionalOptions];
};

/**
 * Base cleanup logic shared between radio buttons and checkboxes
 */
const cleanupFormFieldBase = (
  field: any,
  validAnnotationIds: Set<string>,
  fieldType: string,
): any => {
  const fieldName = field.name || field.id || 'unknown';

  // Validate and clean up options
  field.options = validateAndCleanupOptions(field.options);

  // Validate annotationIds if they exist
  if (field.annotationIds) {
    field.annotationIds = validateAnnotationIds(
      field.annotationIds,
      validAnnotationIds,
      fieldName,
      fieldType,
    );
  }

  return field;
};

/**
 * Cleans up a single radio button form field
 */
const cleanupRadioButtonField = (
  field: any,
  validAnnotationIds: Set<string>,
): any => {
  // Calculate fieldName at the start to match original behavior
  const fieldName = field.name || field.id || 'unknown';
  const fieldType = 'radio button';
  const cleanedField = cleanupFormFieldBase(
    field,
    validAnnotationIds,
    fieldType,
  );

  // Radio buttons need 1:1 alignment between options and annotationIds
  if (cleanedField.annotationIds) {
    cleanedField.options = alignOptionsWithAnnotationIds(
      cleanedField.options,
      cleanedField.annotationIds.length,
      fieldName,
      fieldType,
    );
  }

  return cleanedField;
};

/**
 * Cleans up a single checkbox form field
 */
const cleanupCheckboxField = (
  field: any,
  validAnnotationIds: Set<string>,
): any => {
  const fieldType = 'checkbox';
  const cleanedField = cleanupFormFieldBase(
    field,
    validAnnotationIds,
    fieldType,
  );

  // For checkboxes, ensure at least one option exists
  // Unlike radio buttons, checkboxes don't necessarily need 1:1 alignment
  if (cleanedField.options.length === 0) {
    cleanedField.options = [getDefaultFormOption()];
  }

  return cleanedField;
};

/**
 * Builds a set of valid annotation IDs from the annotations array
 */
const buildValidAnnotationIdsSet = (annotations: any[]): Set<string> => {
  const validAnnotationIds = new Set<string>();
  if (annotations && Array.isArray(annotations)) {
    for (const annotation of annotations) {
      if (annotation?.id != null) {
        validAnnotationIds.add(String(annotation.id));
      }
    }
  }
  return validAnnotationIds;
};

/**
 * Validates if a formFieldValue is valid for its corresponding field
 */
const isValidFormFieldValue = (value: any, formFields: any[]): boolean => {
  const field = formFields.find((f: any) => f.name === value.name);

  if (!field) {
    return false;
  }

  if (field.type === 'pspdfkit/form-field/radio') {
    if (value.value !== 'Off') {
      const validValues = field.options?.map((opt: any) => opt.value) || [];
      return validValues.includes(value.value);
    }
  }

  if (field.type === 'pspdfkit/form-field/checkbox') {
    // For checkboxes, value can be an array or single value
    const rawValue = value.value;
    const valueArray = Array.isArray(rawValue) ? rawValue : [rawValue];

    // Filter out null, undefined, 'Off', and empty values
    const validValueArray = valueArray.filter(
      (v: any) =>
        v !== null &&
        v !== undefined &&
        v !== 'Off' &&
        v !== '' &&
        String(v).toLowerCase() !== 'off',
    );

    // If there are valid values, check if they match field options
    if (validValueArray.length > 0) {
      const validValues = field.options?.map((opt: any) => opt.value) || [];
      return validValueArray.every((v: any) => validValues.includes(v));
    }

    // Empty/Off values are valid for checkboxes (unchecked state)
    return true;
  }

  return true;
};

/**
 * Finds a form field by name
 */
const findFieldByName = (name: string, formFields: any[]): any => {
  return formFields.find((f: any) => f.name === name);
};

/**
 * Handles deduplication for radio button values (keeps only the first one)
 */
const deduplicateRadioValues = (
  value: any,
  seenFieldNames: Set<string>,
): boolean => {
  if (!seenFieldNames.has(value.name)) {
    seenFieldNames.add(value.name);
    return true;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[cleanupFormFields] Removing duplicate formFieldValue for radio button field "${value.name}"`,
  );
  return false;
};

/**
 * Filters and validates checkbox values
 */
const filterCheckboxValues = (value: any, field: any): any | null => {
  const rawValue = value.value;
  const valueArray = Array.isArray(rawValue) ? rawValue : [rawValue];

  // Filter out null, undefined, and invalid option values
  const validValues = field.options?.map((opt: any) => opt.value) || [];
  const filteredValueArray = valueArray.filter(
    (v: any) =>
      v !== null &&
      v !== undefined &&
      v !== '' &&
      (validValues.includes(v) || String(v).toLowerCase() === 'off'),
  );

  // Only return if there are valid values or if it's explicitly 'Off' or null
  if (
    filteredValueArray.length > 0 ||
    value.value === 'Off' ||
    value.value === null
  ) {
    // Update the value to the filtered array (or keep as single value if only one)
    return {
      ...value,
      value:
        filteredValueArray.length === 1
          ? filteredValueArray[0]
          : filteredValueArray.length > 1
            ? filteredValueArray
            : 'Off',
    };
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[cleanupFormFields] Removing formFieldValue with invalid values for checkbox field "${value.name}"`,
  );
  return null;
};

/**
 * Removes duplicate formFieldValues for radio buttons (keeping only the first one)
 * and filters invalid values for checkboxes
 */
const deduplicateFormFieldValues = (
  formFieldValues: any[],
  formFields: any[],
): any[] => {
  const uniqueFormFieldValues: any[] = [];
  const seenRadioFieldNames = new Set<string>();

  for (const value of formFieldValues) {
    const field = findFieldByName(value.name, formFields);

    if (!field) {
      uniqueFormFieldValues.push(value);
      continue;
    }

    if (field.type === 'pspdfkit/form-field/radio') {
      const shouldInclude = deduplicateRadioValues(value, seenRadioFieldNames);
      if (shouldInclude) {
        uniqueFormFieldValues.push(value);
      }
    } else if (field.type === 'pspdfkit/form-field/checkbox') {
      const filteredValue = filterCheckboxValues(value, field);
      if (filteredValue !== null) {
        uniqueFormFieldValues.push(filteredValue);
      }
    } else {
      uniqueFormFieldValues.push(value);
    }
  }

  return uniqueFormFieldValues;
};

const cleanupRadioButtonFormFieldOptions = async (
  instance: Instance,
  formField: any,
) => {
  let needsUpdate = false;
  let cleanedOptions = formField['options'];

  cleanedOptions = cleanedOptions.filter((opt: any) => {
    const isValid = isValidFormOption(opt);

    if (!isValid) {
      needsUpdate = true;
    }

    return isValid;
  });

  if (cleanedOptions.size === 0) {
    cleanedOptions = NutrientViewer.Immutable.List.of(
      new NutrientViewer.FormOption(getDefaultFormOption()),
    );
    needsUpdate = true;
  }

  if (needsUpdate) {
    const updatedField = formField.set('options', cleanedOptions);
    await instance.update(updatedField);
  }
};

const processSignatureAnnotation = async (
  instance: Instance,
  annotation: AnnotationsUnion,
  mySignatureIdsRef: any,
  setSignatureAnnotationIds: any,
) => {
  if (!annotation['isSignature']) {
    return;
  }

  for (let i = 0; i < instance.totalPageCount; i++) {
    const annotations = await instance.getAnnotations(i);
    for await (const maybeCorrectAnnotation of annotations) {
      if (
        annotation.boundingBox.isRectOverlapping(
          maybeCorrectAnnotation.boundingBox,
        )
      ) {
        const newAnnotation = getAnnotationRenderers({
          annotation: maybeCorrectAnnotation,
          instance,
        });
        if (newAnnotation?.node) {
          newAnnotation.node.className = 'signed';
        }
      }
    }
  }

  const signatures = [...mySignatureIdsRef.current, annotation.id];

  setSignatureAnnotationIds(signatures);
  mySignatureIdsRef.current = signatures;
};

export const handleAnnotationCreation = async (
  instance: Instance,
  annotation: AnnotationsUnion,
  mySignatureIdsRef: any,
  setSignatureAnnotationIds: any,
  myEmail: string,
) => {
  await processSignatureAnnotation(
    instance,
    annotation,
    mySignatureIdsRef,
    setSignatureAnnotationIds,
  );
};

export const handleAnnotationDelete = async (
  instance: any,
  annotation: any,
) => {
  if (annotation.isSignature) {
    for (let i = 0; i < instance.totalPageCount; i++) {
      const annotations = await instance.getAnnotations(i);
      for await (const maybeCorrectAnnotation of annotations) {
        if (
          annotation.boundingBox.isRectOverlapping(
            maybeCorrectAnnotation.boundingBox,
          )
        ) {
          const newAnnotation = getAnnotationRenderers({
            annotation: maybeCorrectAnnotation,
            instance,
          });
          if (newAnnotation?.node) {
            newAnnotation.node.className = '';
          }
        }
      }
    }
  }
};

export const handleFormFieldUpdate = async (
  instance: Instance,
  updatedFields: List<FormField>,
) => {
  const radioButtonFields = updatedFields.filter(
    (field: FormField) => field['radiosInUnison'] !== undefined,
  );

  for (const field of radioButtonFields) {
    await cleanupRadioButtonFormFieldOptions(instance, field);
  }
};

export const handleFormFieldValueUpdate = async (
  instance: Instance,
  updatedValues: any,
) => {
  for (const value of updatedValues) {
    try {
      const formFields = await instance.getFormFields();
      if (!formFields) {
        // eslint-disable-next-line no-console
        console.warn(
          `[handleFormFieldValueUpdate] Form fields not found for value "${value.name}"`,
        );
        continue;
      }

      const field = formFields.find((f: any) => f?.name === value.name);
      if (!field) {
        // eslint-disable-next-line no-console
        console.warn(
          `[handleFormFieldValueUpdate] Field "${value.name}" not found`,
        );
        continue;
      }

      if (field?.['type'] === 'pspdfkit/form-field/radio') {
        const validValues = field['options']
          .filter((opt: any) => opt.value !== null)
          .map((opt: any) => opt.value)
          .toArray();

        if (value.value !== 'Off' && !validValues.includes(value.value)) {
          const newValue = field['defaultValue'] || validValues[0] || 'Off';
          const updatedValue = value.set('value', newValue);

          await instance.update(updatedValue);
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error(
        `[handleFormFieldValueUpdate] Error validating radio button value for "${value.name}": ${errorMessage}`,
        error,
      );
    }
  }
};

export const cleanupRadioButtons = (json: any) => {
  if (!json || typeof json !== 'object') {
    console.error(
      '[cleanupFormFields] Invalid input: expected object, got',
      typeof json,
    );
    return json;
  }

  // Create a set of valid annotation IDs for quick lookup
  const validAnnotationIds = buildValidAnnotationIdsSet(json.annotations || []);

  if (json.formFields) {
    json.formFields = json.formFields.map((field: any) => {
      if (field.type === 'pspdfkit/form-field/radio') {
        return cleanupRadioButtonField(field, validAnnotationIds);
      }
      if (field.type === 'pspdfkit/form-field/checkbox') {
        return cleanupCheckboxField(field, validAnnotationIds);
      }
      return field;
    });
  }

  if (json.formFieldValues && json.formFields) {
    // Filter invalid formFieldValues
    const validFormFieldValues = json.formFieldValues.filter((value: any) =>
      isValidFormFieldValue(value, json.formFields),
    );

    // Ensure only one formFieldValue per radio button FormField
    // and filter invalid values for checkboxes
    json.formFieldValues = deduplicateFormFieldValues(
      validFormFieldValues,
      json.formFields,
    );
  }

  return json;
};

export const updateAnnotationIdsBasedOnPdfObjectId = (
  originalInstantJSON: any,
) => {
  const instantJSON = cloneDeep(originalInstantJSON);
  const mappedAnnotation = instantJSON.annotations.map((annotation: any) => {
    // Check if `pdfObjectId` exists
    if (
      (!isUndefined(annotation.pdfObjectId) &&
        typeof annotation.pdfObjectId === 'string') ||
      typeof annotation.pdfObjectId === 'number'
    ) {
      // Update the annotation ID with pdfObjectId
      annotation.id = annotation.pdfObjectId.toString();
    }

    return annotation;
  });

  instantJSON.annotations = mappedAnnotation;

  return instantJSON;
};

export const generateInstantJSON = async (instantJSON: any) => {
  const currentTimestamp = new Date().toISOString();

  instantJSON.formFieldValues = instantJSON.formFieldValues || [];

  const formFieldValues = instantJSON.formFieldValues.map((formField: any) => {
    formField.updatedAt = currentTimestamp;
    return formField;
  });

  instantJSON.formFieldValues = formFieldValues;

  return cleanupRadioButtons(instantJSON);
};

export const extractAnnotationsContacts = (instantJSON: any) => {
  if (!instantJSON.annotations) {
    return [];
  }
  const uniqueContacts: any[] = [];
  const contactMap = new Map();
  instantJSON.annotations.forEach((annotation: any, index: number) => {
    const contact = annotation.customData;

    if (!contact || !contact.name || !contact.signerID) {
      return;
    }

    const key = `${contact.name}-${contact.signerID}`;

    if (!contactMap.has(key)) {
      contactMap.set(key, true);

      const [first_name, last_name] = contact.name.split(' ');

      uniqueContacts.push({
        id: contact.signerID,
        first_name: first_name,
        last_name: last_name,
        email: contact.name,
      });
    }
  });

  return uniqueContacts;
};

export const replacePlaceholdersByContacts = (
  annotations: any,
  contacts: TAssignee[],
  type: string | undefined,
) => {
  if (type !== 'multi' || isUndefined(type) || isNull(type)) {
    return annotations;
  }
  if (
    !Array.isArray(annotations) ||
    annotations.length === 0 ||
    !Array.isArray(contacts)
  ) {
    return annotations;
  }

  return annotations.map((annotation: any) => {
    const customData = annotation.customData;

    if (
      customData?.isPlaceholder === true &&
      (customData?.type === 'signature' ||
        customData?.type === 'initial' ||
        customData?.type === 'signatureWithDateSigned' ||
        customData?.type === 'dateSigned') &&
      typeof customData.name === 'string' &&
      customData.name.startsWith('Contact ')
    ) {
      const match = customData.name.match(/^Contact (\d+)$/);
      const contactIndex = match ? parseInt(match[1], 10) - 1 : -1;
      const contact = contactIndex >= 0 ? contacts[contactIndex] : undefined;

      if (contact) {
        return {
          ...annotation,
          customData: {
            ...customData,
            name: `${contact.first_name} ${contact.last_name}`,
            signerID: contact.id,
            isPlaceholder: false,
          },
        };
      }
    }

    return annotation;
  });
};

export const addDuplicateButton = (
  annotation: any,
  instance: any,
  storeBboxCallback?: (annotationId: string, bbox: number[]) => void,
) => {
  const popupFooterDiv = instance.contentDocument.querySelector(
    '[data-block-id="form-creator-footer"]',
  );

  const existingDuplicateButton =
    instance.contentDocument.querySelector('.duplicate-button');

  if (existingDuplicateButton) {
    existingDuplicateButton.remove();
  }

  if (popupFooterDiv) {
    const duplicateButtonHTML = document.createElement('button');
    const deleteButtonHTML = popupFooterDiv.querySelector(
      '.PSPDFKit-Form-Creator-Editor-Delete',
    );
    const deleteButtonClassName = deleteButtonHTML?.className.replace(
      'PSPDFKit-Form-Creator-Editor-Delete',
      '',
    );

    duplicateButtonHTML.innerHTML = 'Duplicate';
    duplicateButtonHTML.className =
      (deleteButtonClassName ? `${deleteButtonClassName} ` : '') +
      'duplicate-button ' +
      annotation.Id;
    duplicateButtonHTML.onclick = () =>
      duplicateAnnotationCallback(annotation, instance, storeBboxCallback);

    popupFooterDiv.appendChild(duplicateButtonHTML);
  }
};

export const createDebouncedAnnotationHandlers = (
  instance: Instance,
  featureFlags?: { live_form_duplicate_fields?: boolean },
  storeBboxCallback?: (annotationId: string, bbox: number[]) => void,
) => {
  let duplicateButtonTimeout: NodeJS.Timeout | null = null;
  let removeDateTimeFieldTimeout: NodeJS.Timeout | null = null;

  const handleDuplicateButton = (annotation: any) => {
    if (duplicateButtonTimeout) {
      clearTimeout(duplicateButtonTimeout);
    }

    duplicateButtonTimeout = setTimeout(() => {
      if (annotation.customData?.type === 'dateTimeField') {
        const dropdown = instance.contentDocument.querySelector(
          'label[for="dateTimeFormat"]',
        );
        dropdown?.remove();
      }

      if (featureFlags?.live_form_duplicate_fields) {
        addDuplicateButton(annotation, instance, storeBboxCallback);
      }
    }, 100);
  };

  const handleRemoveDateTimeField = (annotation: any) => {
    if (removeDateTimeFieldTimeout) {
      clearTimeout(removeDateTimeFieldTimeout);
    }

    removeDateTimeFieldTimeout = setTimeout(() => {
      if (annotation.customData?.type === 'dateTimeField') {
        const dropdown = instance.contentDocument.querySelector(
          '[data-block-id="form-desiger-content"]',
        );
        dropdown?.children[1]?.remove();
      }
    }, 100);
  };

  const cleanup = () => {
    if (duplicateButtonTimeout) {
      clearTimeout(duplicateButtonTimeout);
      duplicateButtonTimeout = null;
    }
    if (removeDateTimeFieldTimeout) {
      clearTimeout(removeDateTimeFieldTimeout);
      removeDateTimeFieldTimeout = null;
    }
  };

  return {
    handleDuplicateButton,
    handleRemoveDateTimeField,
    cleanup,
  };
};
